import React, { useState, useEffect, lazy, Suspense } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { LanguageProvider } from './context/LanguageContext';
import { AnimatePresence } from 'framer-motion';

// === Eager-Imports: Homepage + globale Bausteine ===
// Diese werden im Initial-Bundle ausgeliefert weil sie auf der Startseite sofort gebraucht werden.
import Header from './components/Header';
import HeroSection from './components/HeroSection';
import AudienceSection from './components/AudienceSection';
import ServicesSection from './components/ServicesSection';
import HowWeWorkSection from './components/HowWeWorkSection';
import CommunitySection from './components/CommunitySection';
import AboutSection from './components/AboutSection';
import BlogSection from './components/BlogSection';
import LinkedInFeedSection from './components/LinkedInFeedSection';
import FAQSection from './components/FAQSection';
import BookingSection from './components/BookingSection';
import MasterclassBanner from './components/MasterclassBanner';
import Footer from './components/Footer';
import CookieConsent from './components/CookieConsent';
import NewsletterSignup from './components/NewsletterSignup';
import ChatWidget from './components/ChatWidget';
import { usePageTracking } from './hooks/useAnalytics';

// === Lazy-Imports: Sub-Routes + Modals (Code-Splitting für Initial-Bundle) ===
// Sebastian-Performance-Phase-3 2026-06-10: spart ~1.2 MB im Initial-Bundle.
// Routes werden erst bei Navigation geladen, Modals erst beim Klick.
const PillarStationaererHandel = lazy(() => import('./components/pillar/PillarStationaererHandel'));
const PillarFuerFilialisten = lazy(() => import('./components/pillar/PillarFuerFilialisten'));
const PillarStrategieRetail = lazy(() => import('./components/pillar/PillarStrategieRetail'));
const PillarBeratungKassel = lazy(() => import('./components/pillar/PillarBeratungKassel'));
const PillarHandel = lazy(() => import('./components/pillar/PillarHandel'));
const PillarGastronomie = lazy(() => import('./components/pillar/PillarGastronomie'));
const PillarHandwerk = lazy(() => import('./components/pillar/PillarHandwerk'));
// Below-the-fold-Sections der Homepage lazy laden (Performance-Boost auf Mobile)
const ROICalculator = lazy(() => import('./components/landing/ROICalculator'));
const RoadFramework = lazy(() => import('./components/RoadFramework'));
const LandingReadyCheck = lazy(() => import('./components/landing/LandingReadyCheck'));
const LandingStrategieWorkshop = lazy(() => import('./components/landing/LandingStrategieWorkshop'));
const LandingMasterclass = lazy(() => import('./components/landing/LandingMasterclass'));
const NewsletterArticle = lazy(() => import('./components/NewsletterArticle'));
const CheckoutSuccess = lazy(() => import('./components/CheckoutSuccess'));
const CheckoutCancelled = lazy(() => import('./components/CheckoutCancelled'));
const AdminPanel = lazy(() => import('./components/AdminPanel'));
const CRMBoard = lazy(() => import('./components/CRMBoard'));
const CustomerPortal = lazy(() => import('./components/CustomerPortal'));
const EmployeeSessionPage = lazy(() => import('./components/EmployeeSessionPage'));
const JohannaPage = lazy(() => import('./components/JohannaPage'));
const KiTeamPage = lazy(() => import('./components/KiTeamPage'));
const ResetPassword = lazy(() => import('./components/ResetPassword'));
const NewsletterConfirm = lazy(() => import('./components/NewsletterConfirm'));
// Modals — auf Homepage sehr selten geöffnet, lazy spart Initial-Bundle
const ImpressumPage = lazy(() => import('./components/ImpressumPage'));
const DatenschutzPage = lazy(() => import('./components/DatenschutzPage'));
const AGBPage = lazy(() => import('./components/AGBPage'));

// Loader-Fallback während lazy-Komponente geladen wird
function RouteLoader() {
  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
      <div
        className="w-10 h-10 border-2 border-[#befa0a]/30 border-t-[#befa0a] rounded-full"
        style={{ animation: 'spin 1s linear infinite' }}
        aria-label="Lädt…"
      />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// Eigene /datenschutz-Route — gleiche Komponente wie das Modal, aber direkt
// als Page erreichbar (LinkedIn App Privacy URL, sameAs-Schema, Email-Footer
// koennen jetzt darauf zeigen). onClose navigiert zur Homepage zurueck.
function DatenschutzRoute() {
  const navigate = useNavigate();
  return <DatenschutzPage onClose={() => navigate('/')} />;
}

// Impressum + AGB analog — gleiche Komponente als Page.
function ImpressumRoute() {
  const navigate = useNavigate();
  return <ImpressumPage onClose={() => navigate('/')} />;
}

function AGBRoute() {
  const navigate = useNavigate();
  return <AGBPage onClose={() => navigate('/')} />;
}

function HomePage() {
  const [showImpressum, setShowImpressum] = useState(false);
  const [showDatenschutz, setShowDatenschutz] = useState(false);
  const [showAGB, setShowAGB] = useState(false);
  usePageTracking();

  useEffect(() => {
    const ds = () => setShowDatenschutz(true);
    const agb = () => setShowAGB(true);
    window.addEventListener('openDatenschutz', ds);
    window.addEventListener('openAGB', agb);
    return () => {
      window.removeEventListener('openDatenschutz', ds);
      window.removeEventListener('openAGB', agb);
    };
  }, []);

  return (
    <>
      <Header />
      <main>
        <HeroSection />
        <ServicesSection />
        <Suspense fallback={<div className="bg-[#0a0a0a] h-32" />}>
          <ROICalculator />
        </Suspense>
        <AudienceSection />
        <Suspense fallback={<div className="bg-[#0a0a0a] h-32" />}>
          <RoadFramework />
        </Suspense>
        <MasterclassBanner />
        <HowWeWorkSection />
        <CommunitySection />
        <AboutSection />
        <BlogSection />
        <FAQSection />
        <LinkedInFeedSection />
        <BookingSection />
        <NewsletterSignup />
      </main>
      <Footer
        onImpressum={() => setShowImpressum(true)}
        onDatenschutz={() => setShowDatenschutz(true)}
        onAGB={() => setShowAGB(true)}
      />
      <CookieConsent onPrivacy={() => setShowDatenschutz(true)} />
      <ChatWidget />
      {/* Lazy-geladene Modals — Suspense mit null-Fallback (kein Flash) */}
      <Suspense fallback={null}>
        <AnimatePresence>
          {showImpressum && <ImpressumPage onClose={() => setShowImpressum(false)} />}
          {showDatenschutz && <DatenschutzPage onClose={() => setShowDatenschutz(false)} />}
          {showAGB && <AGBPage onClose={() => setShowAGB(false)} />}
        </AnimatePresence>
      </Suspense>
    </>
  );
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); return; }
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname, hash]);
  return null;
}

function App() {
  // GA4 wird DSGVO-konform erst in CookieConsent.handleAccept geladen, nicht beim App-Mount.
  return (
    <HelmetProvider>
      <LanguageProvider>
        <div className="App" data-testid="app-root">
          <BrowserRouter>
            <ScrollToTop />
            <Suspense fallback={<RouteLoader />}>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/ki-im-stationaeren-handel" element={<PillarStationaererHandel />} />
                <Route path="/ki-fuer-filialisten" element={<PillarFuerFilialisten />} />
                <Route path="/ki-strategie-retail" element={<PillarStrategieRetail />} />
                <Route path="/ki-beratung-kassel" element={<PillarBeratungKassel />} />
                <Route path="/ki-handel" element={<PillarHandel />} />
                <Route path="/ki-gastronomie" element={<PillarGastronomie />} />
                <Route path="/ki-handwerk" element={<PillarHandwerk />} />
                <Route path="/ki-ready-check" element={<LandingReadyCheck />} />
                <Route path="/ki-strategie-workshop" element={<LandingStrategieWorkshop />} />
                <Route path="/ki-masterclass" element={<LandingMasterclass />} />
                <Route path="/insights/:slug" element={<NewsletterArticle />} />
                <Route path="/checkout/success" element={<CheckoutSuccess />} />
                <Route path="/checkout/cancelled" element={<CheckoutCancelled />} />
                <Route path="/admin" element={<AdminPanel />} />
                <Route path="/crm" element={<CRMBoard />} />
                <Route path="/portal" element={<CustomerPortal />} />
                <Route path="/c/:token" element={<EmployeeSessionPage />} />
                <Route path="/johanna" element={<JohannaPage />} />
                <Route path="/ki-team" element={<KiTeamPage />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/newsletter-confirm" element={<NewsletterConfirm action="confirm" />} />
                <Route path="/newsletter-unsubscribe" element={<NewsletterConfirm action="unsubscribe" />} />
                <Route path="/datenschutz" element={<DatenschutzRoute />} />
                <Route path="/impressum" element={<ImpressumRoute />} />
                <Route path="/agb" element={<AGBRoute />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </div>
      </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
